import moment from "moment";

import { Pharmacy } from "@/Common/Api/Pharmacy/Dto/Responses/Pharmacy";
import { DateTime } from "@/Common/Helpers/DateTime";
import { ConsentPrograms } from "@/Common/Models/ConsentPrograms";
import { DateTimeWithPrecision } from "@/Common/Models/DateTimeWithPrecision";
import { EditMode, PatientTab, ProfileTab } from "@/Common/Models/Patient/PatientEnums";
import { IPatientOtherInformationModel } from "@/Common/Models/Patient/UI/Profile/IPatientOtherInformationModel";
import * as LocalOperations from "@/Common/Operations/operations.local";
import * as LocalPatientOperations from "@/Common/Operations/operations.patient.local";
import { LocalEvent } from "@/Common/Stores/Actions/LocalEvent";
import { BaseOperationHandler } from "@/Common/Stores/Handlers/Local/BaseOperationHandler";
import { DirectoryPharmacy } from "@/ContactDirectory/Models/DirectoryPharmacy";
import { PharmacyProxy } from "@/ContactDirectory/Models/PharmacyProxy";
import { PharmacyDropdownItem } from "@/Patient/Models/PharmacyDropdownItem";
import { PatientStore } from "@/Patient/Stores/PatientStore";

class PatientProfileOtherInfoHandler extends BaseOperationHandler {
    constructor() {
        super();

        this.RegisterOperation(LocalPatientOperations.Profile.OtherInfo_EditClick, this.OtherInfoEditClick);
        this.RegisterOperation(LocalPatientOperations.Profile.OtherInfo_SaveClick, this.OtherInfoSaveClick);
        this.RegisterOperation(LocalPatientOperations.Profile.OtherInfo_CancelClick, this.OtherInfoCancelClick);
        this.RegisterOperation(LocalPatientOperations.Profile.OtherInfo_PharmacySelect, this.OtherInfoPharmacySelect);
        this.RegisterOperation(LocalPatientOperations.Profile.OtherInfo_InsuranceCompanyNameChange, this.OtherInsuranceCompanyNameChange);
        this.RegisterOperation(LocalPatientOperations.Profile.OtherInfo_InsurancePolicyNoChange, this.OtherInsurancePolicyNoChange);
        this.RegisterOperation(LocalPatientOperations.Profile.OtherInfo_LoadPreferredDirectoryPharmacy, this.LoadPreferredDirectoryPharmacy);
        this.RegisterOperation(LocalPatientOperations.Profile.OtherInfo_PharmacyAddressChange, this.OtherInsurancPharmacyAddressChange);
        this.RegisterOperation(LocalPatientOperations.Profile.OtherInfo_PharmacyBannerChange, this.OtherInsurancPharmacyBannerChange);
        this.RegisterOperation(LocalPatientOperations.Profile.OtherInfo_PharmacyPhoneChange, this.OtherInsurancPharmacyPhoneChange);
        this.RegisterOperation(LocalPatientOperations.Profile.OtherInfo_PharmacyFaxChange, this.OtherInsurancPharmacyFaxChange);
        this.RegisterOperation(LocalPatientOperations.Profile.OtherInfo_PharmacyRemove, this.OtherInsurancePharmacyRemove);
        this.RegisterOperation(LocalPatientOperations.Profile.OtherInfo_InsuranceRemove, this.OtherInsuranceInsuranceRemove);
        this.RegisterOperation(LocalPatientOperations.Profile.OtherInfo_PharmacyNameChange, this.OtherInsurancPharmacyNameChange);
        this.RegisterOperation(LocalPatientOperations.Fax.UpdatePreferredPharmacy, this.UpdatePreferredPharmacy);
        this.RegisterOperation(LocalPatientOperations.Profile.OtherInfo_SearchStringChange, this.SearchStringChange);
        this.RegisterOperation(LocalPatientOperations.Profile.OtherInfo_AddConsentPopup, this.addConsentPopup);
        this.RegisterOperation(LocalPatientOperations.Profile.OtherInfo_CloseConsentPopup, this.closeConsentPopup);
        this.RegisterOperation(LocalPatientOperations.Profile.OtherInfo_SelectConsentProgram, this.selectConsentProgram);
        this.RegisterOperation(LocalPatientOperations.Profile.OtherInfo_OnDocGivenClick, this.onDocGivenClick);
        this.RegisterOperation(LocalPatientOperations.Profile.OtherInfo_OnDidntRespondClick, this.onDidntRespondClick);
        this.RegisterOperation(LocalPatientOperations.Profile.OtherInfo_OnHasConsentClick, this.onHasConsentClick);
        this.RegisterOperation(LocalPatientOperations.Profile.OtherInfo_OnHasRefusedClick, this.onHasRefusedClick);
        this.RegisterOperation(LocalPatientOperations.Profile.OtherInfo_OnHasRevokedClick, this.onHasRevokedClick);
        this.RegisterOperation(LocalPatientOperations.Profile.OtherInfo_OnRemoveConsentClick, this.onRemoveConsentClick);
        this.RegisterOperation(LocalPatientOperations.Profile.OtherInfo_AddConsentProgramClick, this.addConsentProgramClick);
        this.RegisterOperation(LocalPatientOperations.Profile.OtherInfo_ChangeDocGivenDate, this.changeDocGivenDate);
        this.RegisterOperation(LocalPatientOperations.Profile.OtherInfo_ChangeConsentRefuseDate, this.changeConsentRefuseDate);
        this.RegisterOperation(LocalPatientOperations.Profile.OtherInfo_ChangeConsentRevokeDate, this.changeConsentRevokeDate);
        this.RegisterOperation(LocalPatientOperations.Profile.OtherInfo_ModifyConsentClick, this.modifyConsentClick);
        this.RegisterOperation(LocalPatientOperations.Profile.OtherInfo_UpdateConsentProgram, this.updateConsentProgram);
        this.RegisterOperation(LocalPatientOperations.Profile.OtherInfo_TodayClickSelectedConsent, this.todayClickSelectedConsent);
        this.RegisterOperation(LocalPatientOperations.Profile.OtherInfo_TodayClickDocGiven, this.todayClickDocGiven);
        this.RegisterOperation(LocalPatientOperations.Profile.OtherInfo_TodayClickConsentRevoked, this.todayClickConsentRevoked);
        this.RegisterOperation(LocalPatientOperations.Profile.OtherInfo_LoadConsentProgramHistory, this.loadConsentProgramHistory);
        this.RegisterOperation(LocalPatientOperations.Profile.OtherInfo_AddConsentProgramDialog, this.addConsentProgramDialog);
        this.RegisterOperation(LocalPatientOperations.Profile.OtherInfo_CloseConsentProgramDialog, this.closeConsentProgramDialog);
        this.RegisterOperation(LocalOperations.Misc.MyleApp_Container_Click, this.overlayClick, false);
        this.RegisterOperation(LocalPatientOperations.Profile.OtherInfo_Redirect, this.redirectToOtherInfo);
    }

    private OtherInfoEditClick(request: LocalEvent, store: PatientStore): void {
        let uiModel = store.PatientUIModels[request.PatientId];
        let model: IPatientOtherInformationModel = store.PatientProfileOtherIdentificationModels[request.PatientId];

        let modelToUndo = model.set("PatientContactId", model.get("PatientContactId")); // to get copy/clone of immutable model
        model = model.set("SearchString", model.get("PreferredPharmacy")?.CustomPharmacy?.Banner ?? "");
        uiModel = uiModel.set("PatientProfileEditMode", EditMode.Edit);

        store.PatientProfileOtherIdentificationModels[request.PatientId] = model;
        store.PatientProfileOtherIdentificationUndoModels[request.PatientId] = modelToUndo;
        store.PatientUIModels[request.PatientId] = uiModel;
    }

    private OtherInfoSaveClick(request: LocalEvent, store: PatientStore): void {
        let uiModel = store.PatientUIModels[request.PatientId];
        let otherInfoModel: IPatientOtherInformationModel = store.PatientProfileOtherIdentificationModels[request.PatientId];
        store.ActionsProfile.savePatientOtherInfo(uiModel, otherInfoModel);
    }

    public OtherInfoCancelClick(request: LocalEvent, store: PatientStore): void {
        let uiModel = store.PatientUIModels[request.PatientId];
        let undoModel = store.PatientProfileOtherIdentificationUndoModels[request.PatientId];
        uiModel = uiModel.set("PatientProfileEditMode", EditMode.View);

        store.PatientProfileOtherIdentificationModels[request.PatientId] = undoModel;
        store.PatientUIModels[request.PatientId] = uiModel;
        delete store.PatientProfileOtherIdentificationUndoModels[request.PatientId];
    }

    private async LoadPreferredDirectoryPharmacy(request: LocalEvent, store: PatientStore): Promise<void> {
        const patientContactId = request.Get<number>("patientContactId");
        const patient = store.PatientModels[patientContactId];
        const preferredPharmacy = patient?.PreferredPharmacy;

        if (preferredPharmacy?.DirectoryPharmacyId) {
            await preferredPharmacy.loadDirectoryPharmacy();
            let otherInfoModel = store.PatientProfileOtherIdentificationModels[patientContactId];
            otherInfoModel = otherInfoModel.set("PreferredPharmacy", preferredPharmacy);
            store.PatientProfileOtherIdentificationModels[patientContactId] = otherInfoModel;
        }
    }

    private OtherInfoPharmacySelect(request: LocalEvent, store: PatientStore): void {
        const pharmacy = request.Get<DirectoryPharmacy | PharmacyDropdownItem>("value");
        let otherInfoModel = store.PatientProfileOtherIdentificationModels[request.PatientId];

        let pharmacyProxy: PharmacyProxy;

        if (pharmacy instanceof DirectoryPharmacy && cacheStore.ClinicSettings.EnableContactDirectory) {
            pharmacyProxy = new PharmacyProxy({
                directoryPharmacy: pharmacy,
                directoryPharmacyId: pharmacy?.id
            });
        }
        else if (pharmacy instanceof PharmacyDropdownItem) {
            pharmacyProxy = new PharmacyProxy({
                customPharmacy: pharmacy.Pharmacy
            });
        }

        otherInfoModel = otherInfoModel
            .set("PreferredPharmacy", pharmacyProxy)
            .set("SearchString", pharmacyProxy?.getPharmacyInfo()?.Banner);
        store.PatientProfileOtherIdentificationModels[request.PatientId] = otherInfoModel;
    }

    private OtherInsuranceCompanyNameChange(request: LocalEvent, store: PatientStore): void {
        let val = request.Get<string>("value");

        let otherInfoModel = store.PatientProfileOtherIdentificationModels[request.PatientId];
        otherInfoModel = otherInfoModel.set("InsuranceCompanyName", val);
        store.PatientProfileOtherIdentificationModels[request.PatientId] = otherInfoModel;
    }

    private OtherInsurancePolicyNoChange(request: LocalEvent, store: PatientStore): void {
        let val = request.Get<string>("value");
        let otherInfoModel = store.PatientProfileOtherIdentificationModels[request.PatientId];
        otherInfoModel = otherInfoModel.set("InsurancePolicyNo", val);
        store.PatientProfileOtherIdentificationModels[request.PatientId] = otherInfoModel;
    }

    private OtherInsurancPharmacyAddressChange(request: LocalEvent, store: PatientStore): void {
        let val = request.Get<string>("value");
        let otherInfoModel = store.PatientProfileOtherIdentificationModels[request.PatientId];
        otherInfoModel = this.setPreferredPharmacyField({ Address: val }, otherInfoModel);
        store.PatientProfileOtherIdentificationModels[request.PatientId] = otherInfoModel;
    }

    private OtherInsurancPharmacyPhoneChange(request: LocalEvent, store: PatientStore): void {
        let val = request.Get<string>("value");
        let otherInfoModel = store.PatientProfileOtherIdentificationModels[request.PatientId];
        otherInfoModel = this.setPreferredPharmacyField({ Phone: val }, otherInfoModel);
        store.PatientProfileOtherIdentificationModels[request.PatientId] = otherInfoModel;
    }

    private OtherInsurancPharmacyFaxChange(request: LocalEvent, store: PatientStore): void {
        let val = request.Get<string>("value");
        let otherInfoModel = store.PatientProfileOtherIdentificationModels[request.PatientId];
        otherInfoModel = this.setPreferredPharmacyField({ Fax: val }, otherInfoModel);
        store.PatientProfileOtherIdentificationModels[request.PatientId] = otherInfoModel;
    }

    private OtherInsurancPharmacyNameChange(request: LocalEvent, store: PatientStore): void {
        let val = request.Get<string>("value");
        let otherInfoModel = store.PatientProfileOtherIdentificationModels[request.PatientId];
        otherInfoModel = this.setPreferredPharmacyField({ Name: val }, otherInfoModel);
        store.PatientProfileOtherIdentificationModels[request.PatientId] = otherInfoModel;
    }

    private OtherInsurancPharmacyBannerChange(request: LocalEvent, store: PatientStore): void {
        let val = request.Get<string>("value");
        let otherInfoModel = store.PatientProfileOtherIdentificationModels[request.PatientId];
        otherInfoModel = this.setPreferredPharmacyField({ Banner: val }, otherInfoModel);
        store.PatientProfileOtherIdentificationModels[request.PatientId] = otherInfoModel;
    }

    private UpdatePreferredPharmacy(request: LocalEvent, store: PatientStore): void {
        const pharmacy = request.Get<Pharmacy>("pharmacy");
        const directoryPharmacy = request.Get<DirectoryPharmacy>("directoryPharmacy");

        const pharmacyProxy = new PharmacyProxy({
            customPharmacy: pharmacy,
            directoryPharmacy: directoryPharmacy,
            directoryPharmacyId: directoryPharmacy?.id
        });

        const otherIdentificationModel = store.PatientProfileOtherIdentificationModels[request.PatientId]
            .set("PreferredPharmacy", pharmacyProxy);

        const patientId = request.Get<string | null>("patientContactId") ?? request.PatientId;
        store.PatientProfileOtherIdentificationModels[patientId] = otherIdentificationModel;
        store.PatientModels[patientId].PreferredPharmacy = pharmacyProxy;
    }

    private OtherInsurancePharmacyRemove(request: LocalEvent, store: PatientStore): void {
        const otherInfoModel = store.PatientProfileOtherIdentificationModels[request.PatientId]
            .set("PreferredPharmacy", null)
            .set("SearchString", "");

        store.PatientProfileOtherIdentificationModels[request.PatientId] = otherInfoModel;
    }

    private OtherInsuranceInsuranceRemove(request: LocalEvent, store: PatientStore): void {
        let otherInfoModel = store.PatientProfileOtherIdentificationModels[request.PatientId];

        otherInfoModel = otherInfoModel.set("InsuranceCompanyName", "");
        otherInfoModel = otherInfoModel.set("InsurancePolicyNo", "");

        store.PatientProfileOtherIdentificationModels[request.PatientId] = otherInfoModel;
    }

    private SearchStringChange(request: LocalEvent, store: PatientStore): void {
        let otherInfoModel = store.PatientProfileOtherIdentificationModels[request.PatientId];

        otherInfoModel = otherInfoModel.set("SearchString", request.Get<string>("value"));

        store.PatientProfileOtherIdentificationModels[request.PatientId] = otherInfoModel;
    }

    private addConsentPopup(request: LocalEvent, store: PatientStore): void {
        let uiModel = store.PatientUIModels[request.PatientId];
        let modelToUndo: IPatientOtherInformationModel = store.PatientProfileOtherIdentificationModels[request.PatientId];

        modelToUndo = modelToUndo.set("PatientContactId", modelToUndo.get("PatientContactId")); // to get copy/clone of immutable model
        uiModel = uiModel.set("PatientProfileEditMode", EditMode.Edit);

        store.PatientProfileOtherIdentificationUndoModels[request.PatientId] = modelToUndo;
        store.PatientUIModels[request.PatientId] = uiModel;

        store.ActionsProfile.loadConsentProgram();
    }

    private closeConsentPopup(request: LocalEvent, store: PatientStore): void {
        let otherInfoModel = store.PatientProfileOtherIdentificationModels[request.PatientId];
        otherInfoModel = otherInfoModel.set("ConsentProgramPopup", false);

        otherInfoModel = this.clearConsentPopup(otherInfoModel);

        store.PatientProfileOtherIdentificationModels[request.PatientId] = otherInfoModel;

        sessionStore.hideGlobalOverlay();
    }

    private selectConsentProgram(request: LocalEvent, store: PatientStore): void {
        let consentId = request.Get<number>("value");

        let otherInfoModel = store.PatientProfileOtherIdentificationModels[request.PatientId];
        let consents = otherInfoModel.get("ConsentPrograms").toArray();

        otherInfoModel = otherInfoModel.set("SelectedConsentProgram", new ConsentPrograms(consents.filter(b => b.model.Id.toString() === consentId.toString())[0].model));
        otherInfoModel = otherInfoModel.set("SelectedConsentProgramId", consentId);

        store.PatientProfileOtherIdentificationModels[request.PatientId] = otherInfoModel;
    }

    private onDocGivenClick(request: LocalEvent, store: PatientStore): void {
        let otherInfoModel = store.PatientProfileOtherIdentificationModels[request.PatientId];
        otherInfoModel = otherInfoModel.set("SelectedConsentProgramDocGiven", !otherInfoModel.get("SelectedConsentProgramDocGiven"));

        if (otherInfoModel.get("SelectedConsentProgramDocGiven") === false) {
            otherInfoModel = otherInfoModel.set("DocGivenDate", null);
            otherInfoModel = otherInfoModel.set("DocGivenDateText", "");
        }
        else if (otherInfoModel.get("SelectedConsentProgramDocGiven") === true) {
            var dateNow = DateTime.nowDeprecated();

            otherInfoModel = otherInfoModel.set("DocGivenDate", dateNow);
            otherInfoModel = otherInfoModel.set("DocGivenDateText", moment(dateNow).format("YYYY-MM-DD"));
        }

        store.PatientProfileOtherIdentificationModels[request.PatientId] = otherInfoModel;
    }

    private onDidntRespondClick(request: LocalEvent, store: PatientStore): void {
        let otherInfoModel = store.PatientProfileOtherIdentificationModels[request.PatientId];
        otherInfoModel = otherInfoModel.set("SelectedConsentProgramDidntRespond", true);

        otherInfoModel = otherInfoModel.set("SelectedConsentProgramHasConsented", false);
        otherInfoModel = otherInfoModel.set("SelectedConsentProgramHasRefused", false);
        otherInfoModel = otherInfoModel.set("SelectedConsentProgramHasRevoked", false);
        otherInfoModel = otherInfoModel.set("HasRevokedDate", null);
        otherInfoModel = otherInfoModel.set("HasRevokedDateText", "");

        if (otherInfoModel.get("HasConsentedOrRefusedOrDidntRespondDate") == null) {
            let dateNow = DateTime.nowDeprecated();

            otherInfoModel = otherInfoModel.set("HasConsentedOrRefusedOrDidntRespondDate", dateNow);
            otherInfoModel = otherInfoModel.set("HasConsentedOrRefusedOrDidntRespondDateText", moment(dateNow).format("YYYY-MM-DD"));
        }

        store.PatientProfileOtherIdentificationModels[request.PatientId] = otherInfoModel;
    }

    private onHasConsentClick(request: LocalEvent, store: PatientStore): void {
        let otherInfoModel = store.PatientProfileOtherIdentificationModels[request.PatientId];
        otherInfoModel = otherInfoModel.set("SelectedConsentProgramHasConsented", true);

        otherInfoModel = otherInfoModel.set("SelectedConsentProgramDidntRespond", false);
        otherInfoModel = otherInfoModel.set("SelectedConsentProgramHasRefused", false);

        if (otherInfoModel.get("HasConsentedOrRefusedOrDidntRespondDate") == null) {
            let dateNow = DateTime.nowDeprecated();

            otherInfoModel = otherInfoModel.set("HasConsentedOrRefusedOrDidntRespondDate", dateNow);
            otherInfoModel = otherInfoModel.set("HasConsentedOrRefusedOrDidntRespondDateText", moment(dateNow).format("YYYY-MM-DD"));
        }

        store.PatientProfileOtherIdentificationModels[request.PatientId] = otherInfoModel;
    }

    private onHasRefusedClick(request: LocalEvent, store: PatientStore): void {
        let otherInfoModel = store.PatientProfileOtherIdentificationModels[request.PatientId];
        otherInfoModel = otherInfoModel.set("SelectedConsentProgramHasRefused", true);

        otherInfoModel = otherInfoModel.set("SelectedConsentProgramDidntRespond", false);
        otherInfoModel = otherInfoModel.set("SelectedConsentProgramHasConsented", false);
        otherInfoModel = otherInfoModel.set("SelectedConsentProgramHasRevoked", false);
        otherInfoModel = otherInfoModel.set("HasRevokedDate", null);
        otherInfoModel = otherInfoModel.set("HasRevokedDateText", "");

        if (otherInfoModel.get("HasConsentedOrRefusedOrDidntRespondDate") == null) {
            let dateNow = DateTime.nowDeprecated();

            otherInfoModel = otherInfoModel.set("HasConsentedOrRefusedOrDidntRespondDate", dateNow);
            otherInfoModel = otherInfoModel.set("HasConsentedOrRefusedOrDidntRespondDateText", moment(dateNow).format("YYYY-MM-DD"));
        }

        store.PatientProfileOtherIdentificationModels[request.PatientId] = otherInfoModel;
    }

    private onHasRevokedClick(request: LocalEvent, store: PatientStore): void {
        let otherInfoModel = store.PatientProfileOtherIdentificationModels[request.PatientId];
        otherInfoModel = otherInfoModel.set("SelectedConsentProgramHasRevoked", !otherInfoModel.get("SelectedConsentProgramHasRevoked"));

        if (otherInfoModel.get("SelectedConsentProgramHasRevoked") === true) {
            if (otherInfoModel.get("HasRevokedDate") == null) {
                let dateNow = DateTime.nowDeprecated();

                otherInfoModel = otherInfoModel.set("HasRevokedDate", dateNow);
                otherInfoModel = otherInfoModel.set("HasRevokedDateText", moment(dateNow).format("YYYY-MM-DD"));
            }
        }
        else {
            otherInfoModel = otherInfoModel.set("HasRevokedDate", null);
            otherInfoModel = otherInfoModel.set("HasRevokedDateText", "");
        }

        store.PatientProfileOtherIdentificationModels[request.PatientId] = otherInfoModel;
    }

    private onRemoveConsentClick(request: LocalEvent, store: PatientStore): void {
        let otherInfoModel = store.PatientProfileOtherIdentificationModels[request.PatientId];

        otherInfoModel = this.clearConsentPopup(otherInfoModel);

        store.PatientProfileOtherIdentificationModels[request.PatientId] = otherInfoModel;
    }

    private addConsentProgramClick(request: LocalEvent, store: PatientStore): void {
        store.ActionsProfile.addConsentProgram(store.PatientProfileOtherIdentificationModels[request.PatientId], request.PatientId);
    }

    private changeDocGivenDate(request: LocalEvent, store: PatientStore): void {
        let date = request.Get<DateTimeWithPrecision>("value");

        let otherInfoModel = store.PatientProfileOtherIdentificationModels[request.PatientId];

        if (date && date.value) {
            otherInfoModel = otherInfoModel.set("DocGivenDate", date.value);
            otherInfoModel = otherInfoModel.set("DocGivenDateText", date.text);
        }
        else if (date && IsNullOrWhiteSpace(date.text)) {
            otherInfoModel = otherInfoModel.set("DocGivenDate", null);
            otherInfoModel = otherInfoModel.set("DocGivenDateText", "");
        }
        store.PatientProfileOtherIdentificationModels[request.PatientId] = otherInfoModel;
    }

    private changeConsentRefuseDate(request: LocalEvent, store: PatientStore): void {
        let date = request.Get<DateTimeWithPrecision>("value");

        let otherInfoModel = store.PatientProfileOtherIdentificationModels[request.PatientId];
        if (date && date.value) {
            otherInfoModel = otherInfoModel.set("HasConsentedOrRefusedOrDidntRespondDate", date.value);
            otherInfoModel = otherInfoModel.set("HasConsentedOrRefusedOrDidntRespondDateText", date.text);
        }
        else if (date && IsNullOrWhiteSpace(date.text)) {
            otherInfoModel = otherInfoModel.set("HasConsentedOrRefusedOrDidntRespondDate", null);
            otherInfoModel = otherInfoModel.set("HasConsentedOrRefusedOrDidntRespondDateText", "");
        }

        store.PatientProfileOtherIdentificationModels[request.PatientId] = otherInfoModel;
    }

    private changeConsentRevokeDate(request: LocalEvent, store: PatientStore): void {
        let date = request.Get<DateTimeWithPrecision>("value");

        let otherInfoModel = store.PatientProfileOtherIdentificationModels[request.PatientId];
        if (date && date.value) {
            otherInfoModel = otherInfoModel.set("HasRevokedDate", date.value);
            otherInfoModel = otherInfoModel.set("HasRevokedDateText", date.text);
        }
        else if (date && IsNullOrWhiteSpace(date.text)) {
            otherInfoModel = otherInfoModel.set("HasRevokedDate", null);
            otherInfoModel = otherInfoModel.set("HasRevokedDateText", "");
        }

        store.PatientProfileOtherIdentificationModels[request.PatientId] = otherInfoModel;
    }

    private clearConsentPopup(model: IPatientOtherInformationModel): IPatientOtherInformationModel {
        model = model.set("SelectedConsentProgram", null);
        model = model.set("SelectedConsentProgramId", null);
        model = model.set("SelectedConsentProgramDocGiven", false);
        model = model.set("SelectedConsentProgramHasRefused", false);
        model = model.set("SelectedConsentProgramHasConsented", false);
        model = model.set("SelectedConsentProgramDidntRespond", false);
        model = model.set("SelectedConsentProgramHasRevoked", false);
        model = model.set("HasConsentedOrRefusedOrDidntRespondDate", null);
        model = model.set("HasConsentedOrRefusedOrDidntRespondDateText", "");
        model = model.set("HasRevokedDate", null);
        model = model.set("HasRevokedDateText", "");
        model = model.set("DocGivenDate", null);
        model = model.set("DocGivenDateText", "");

        return model;
    }

    private modifyConsentClick(request: LocalEvent, store: PatientStore): void {
        let otherInfoModel = store.PatientProfileOtherIdentificationModels[request.PatientId];

        otherInfoModel = otherInfoModel.set("ModifyingProgramId", request.Get<number>("value"));
        store.PatientProfileOtherIdentificationModels[request.PatientId] = otherInfoModel;

        store.ActionsProfile.loadConsentProgramForEdit();
    }

    private updateConsentProgram(request: LocalEvent, store: PatientStore): void {
        store.ActionsProfile.updateConsentProgram(store.PatientProfileOtherIdentificationModels[request.PatientId], request.PatientId);
    }

    private todayClickSelectedConsent(request: LocalEvent, store: PatientStore): void {
        let otherInfoModel = store.PatientProfileOtherIdentificationModels[request.PatientId];
        let dateNow = DateTime.nowDeprecated();

        otherInfoModel = otherInfoModel.set("HasConsentedOrRefusedOrDidntRespondDate", dateNow);
        otherInfoModel = otherInfoModel.set("HasConsentedOrRefusedOrDidntRespondDateText", moment(dateNow).format("YYYY-MM-DD"));

        store.PatientProfileOtherIdentificationModels[request.PatientId] = otherInfoModel;
    }

    private todayClickDocGiven(request: LocalEvent, store: PatientStore): void {
        let otherInfoModel = store.PatientProfileOtherIdentificationModels[request.PatientId];
        let dateNow = DateTime.nowDeprecated();

        otherInfoModel = otherInfoModel.set("DocGivenDate", dateNow);
        otherInfoModel = otherInfoModel.set("DocGivenDateText", moment(dateNow).format("YYYY-MM-DD"));

        store.PatientProfileOtherIdentificationModels[request.PatientId] = otherInfoModel;
    }

    private todayClickConsentRevoked(request: LocalEvent, store: PatientStore): void {
        let otherInfoModel = store.PatientProfileOtherIdentificationModels[request.PatientId];
        let dateNow = DateTime.nowDeprecated();

        otherInfoModel = otherInfoModel.set("HasRevokedDate", dateNow);
        otherInfoModel = otherInfoModel.set("HasRevokedDateText", moment(dateNow).format("YYYY-MM-DD"));

        store.PatientProfileOtherIdentificationModels[request.PatientId] = otherInfoModel;
    }

    private loadConsentProgramHistory(request: LocalEvent, store: PatientStore): void {
        store.ActionsProfile.loadConsentProgramHistory(request.PatientId);
    }

    private addConsentProgramDialog(request: LocalEvent, store: PatientStore): void {
        let uiModel = store.PatientUIModels[request.PatientId];

        if (uiModel.get("PatientProfileEditMode") === EditMode.View) {
            let modelToUndo: IPatientOtherInformationModel = store.PatientProfileOtherIdentificationModels[request.PatientId];
            modelToUndo = modelToUndo.set("PatientContactId", modelToUndo.get("PatientContactId"));
            store.PatientProfileOtherIdentificationUndoModels[request.PatientId] = modelToUndo;
        }

        uiModel = uiModel.set("PatientProfileEditMode", EditMode.Edit);
        store.PatientUIModels[request.PatientId] = uiModel;

        let otherInfoModel = store.PatientProfileOtherIdentificationModels[request.PatientId];
        otherInfoModel = otherInfoModel.set("ConsentProgramDialog", true);
        store.PatientProfileOtherIdentificationModels[request.PatientId] = otherInfoModel;
    }

    private closeConsentProgramDialog(request: LocalEvent, store: PatientStore): void {
        let otherInfoModel = store.PatientProfileOtherIdentificationModels[request.PatientId];
        otherInfoModel = otherInfoModel.set("ConsentProgramDialog", false);
        store.PatientProfileOtherIdentificationModels[request.PatientId] = otherInfoModel;
    }

    private overlayClick(request: LocalEvent, store: PatientStore): void {
        let otherInfoModel = store.PatientProfileOtherIdentificationModels[request.PatientId];
        if (otherInfoModel && otherInfoModel.get("ConsentProgramPopup") === true) {
            this.closeConsentPopup(request, store);
            store.EmitChangeToListeners();
        }
    }

    private setPreferredPharmacyField(field: Partial<Pharmacy>, otherInfoModel: IPatientOtherInformationModel): IPatientOtherInformationModel {
        return otherInfoModel.set(
            "PreferredPharmacy",
            new PharmacyProxy({
                ...otherInfoModel.get("PreferredPharmacy"),
                customPharmacy: {
                    ...otherInfoModel.get("PreferredPharmacy")?.CustomPharmacy,
                    ...field
                }
            })
        );
    }

    private redirectToOtherInfo(request: LocalEvent, store: PatientStore): void {
        const { PatientId } = request;
        store.PatientUIModels[PatientId] = store.PatientUIModels[PatientId]
            .set("ActiveVerticalTab", PatientTab.Profile)
            .set("PatientProfileActiveTab", ProfileTab.Other);

        const switchToEditMode = request.Get<boolean>("switchToEditMode");
        if (switchToEditMode) {
            this.OtherInfoEditClick(request, store);
        }
    }
}

export { PatientProfileOtherInfoHandler };
